import TagManager from 'shared/controllers/TagManager';
import EnvConfig from 'shared/libraries/mwc/envConfig';
import { apiFetch } from 'shared/libraries/auth';
import CoreUtils from 'shared/libraries/mwc/utils/coreUtils';
import AssetCacher from 'shared/libraries/mwc/3d/nodeGraph/assetCacher';
import { Product } from 'shared/controllers/ProductSearch';

const AssetStatus = Object.freeze({
    ACTIVE: 0,
    DEPRECATED: 3,
    DRAFT: 4
});

export enum AssetType {
    None = 0,
    Material = 1,
    Fixture = 2,
    Profile = 3,
    Procedural = 4
}

const ASSET_DOWNLOAD_FLAGS = 'include_download_url=true&include_deleted=true&include_not_processed=true';

export default class AssetManager {
    private static instance: AssetManager | undefined = undefined;
    private assets = {};
    private invalidAssetIdsToIgnore = {};
    private loadingPromises = {};

    constructor() {
        if(AssetManager.instance)
            return AssetManager.instance;
        AssetManager.instance = this;

        this.assets = {};

        // Keep track of these, otherwise we'll be pinging the
        // asset info API forever
        this.invalidAssetIdsToIgnore = {};
        this.loadingPromises = {};       
    }

    static getInstance() {
        return AssetManager.instance;
    }

    static normalizeTags(asset)
	{
        let tagsDict = {};
        if(asset.tags)
		{
            asset.tags.forEach((tag) => {
                if(tag.indexOf(':') < 0)
                    return;
                let split = tag.split(':');
                if(tagsDict[split[0]])
                    console.warn(`Duplicate tag keys detected on ${asset.id} for key "${split[0]}"!`);
                else
                    tagsDict[split[0]] = split.slice(1).join(':');
            });
		}
        return tagsDict;
	}

    addAsset(asset) {
        if(asset.tags.includes('hide_in_walter'))
            return;

        // Convert tags with a : to a dict:
        asset.tagsDict = AssetManager.normalizeTags(asset);
        if(!this.assets[asset.asset_type])
            this.assets[asset.asset_type] = [];
        this.assets[asset.asset_type].push(asset);
    }

    private addAssetFromInfo(assetInfo)
    {
        if(!assetInfo.asset_type)
            return;

        let sourceTags = assetInfo.tags.length > 0 ? assetInfo.tags : assetInfo.metadata.tags;
        if(!sourceTags)
            sourceTags = [];
        let name = assetInfo.name;
        if(!name)
            name = assetInfo.metadata?.name ? assetInfo.metadata?.name : assetInfo.levels_of_detail?.[0]?.name;
        if(!name)
            name = 'Untitled';

        // Make it nice
        name = CoreUtils.nicefyNameForUI(name);

        // Extract file names and make them compatible with existing structures:
        let jsonName = null;
        let bjsName = null;
        if(assetInfo.asset_type == AssetType.Material || assetInfo.asset_type == AssetType.Fixture)
        {
            if(assetInfo.levels_of_detail?.length > 0)
			{
                const bIsLoadingMaterial = assetInfo.asset_type == AssetType.Material;
                const lodIndex = AssetCacher.getInstance().getLODLevel(bIsLoadingMaterial, assetInfo['levels_of_detail']);

                const lodFiles = assetInfo.levels_of_detail[lodIndex].files;
                bjsName = lodFiles['gltf'];

                if(AssetCacher.getInstance().isLoadingBakedGltf() && lodFiles['gltf_baked'] !== undefined) {
                    bjsName = lodFiles['gltf_baked'];
                }
			}

            if(!bjsName)
                return;
        }
        else if(assetInfo.asset_type == AssetType.Profile || assetInfo.asset_type == AssetType.Procedural)
        {
            if(!assetInfo.json_file)
                return;

            jsonName = assetInfo.json_file.file_url;
        }

        const asset = {
            ...assetInfo,
            image: assetInfo.thumbnail_url,
            tags: sourceTags,
            name: name,
            categories: assetInfo.categories ? assetInfo.categories : [],
            status: AssetStatus.ACTIVE,
            json_filename: jsonName,
            babylon_filename: bjsName,
        }
        this.addAsset(asset);
    }

    getByName(name): any {

        let foundAsset = null;
        for(let assetType in this.assets)
		{
            foundAsset = this.assets[assetType].find((asset) => { return asset.name === name; });
            if(foundAsset)
                break;
		}

        return foundAsset;
    }

    getById(idIntOrStr): any {
        const id = typeof idIntOrStr === 'string' ? parseInt(idIntOrStr, 10) : idIntOrStr;

        let foundAsset = null;
        for(let assetType in this.assets)
		{
            foundAsset = this.assets[assetType].find((asset) => { return asset.id === id; });
            if(foundAsset)
                break;
		}

        return foundAsset;
    }

    getAssetsOfType(assetType: AssetType, currentOnly = false, withThumbnailsOnly = false, removeDeleted = false): any[] {
        let assets = this.assets[assetType] ? this.assets[assetType] : [];
        return assets.filter((asset) => {
            if(currentOnly && asset.status === AssetStatus.DEPRECATED)
                return false;

            // Skip procedural assets without thumbnails, since these are in the non-working
            // condition at the moment.
            if(withThumbnailsOnly && !asset.thumbnail_url)
                return false;             

            if(removeDeleted && asset.is_deleted)
                return false;

            return true;
        });
	}

    getAssetsOfTypeWithTags(type: AssetType, tags): any[] {
        return this.getAssetsOfType(type)
            .filter(asset => tags.every((tag) => {
                const hasTag = TagManager.hasTag(asset.tags, tag);
                const isOfCategory = asset.categories.find(category => category.name.name === tag) !== undefined;
                return hasTag || isOfCategory;
            }));
    }

    getAssetsOfTypeWithCategory(assetType: AssetType, category: string | string[], currentOnly = false,
                                withThumbnailsOnly = false, removeDeleted = false): any[] {
        let categories = typeof(category) === 'string' ? [category.toLowerCase()] : category.map(c => c.toLowerCase());
        let assets = this.getAssetsOfType(assetType, currentOnly, withThumbnailsOnly, removeDeleted);
        return assets.filter((asset) => {
            let assetCat = asset.tagsDict['category'];
            return (assetCat && categories.indexOf(assetCat.toLowerCase()) >= 0) ||
                categories.findIndex((c) => asset.name.toLowerCase().includes(c)) >= 0;
        });
    }

    getMaterialFromCategories(categoryNames): any[] {
        let materials = [];
        categoryNames.forEach((categoryName) => {
            const materialsForCategory = this.getAssetsOfType(AssetType.Material).filter((material) => {
                const categories = material.categories
                    .map(category => category.name.name)
                    .filter(categoryNameName => (categoryNameName === categoryName));
                return (categories.length > 0);
            });
            materials = materials.concat(materialsForCategory);
        });
        return materials;
    }
       
    getPathsOfType(type): any[] {
        return this.getAssetsOfType(AssetType.Profile).filter(a => a.name.toLowerCase().includes(type));
    }

    private getAssetInfos(assetIds)
    {
        assetIds = assetIds.filter(assetId => assetId !== null && assetId !== undefined);
        let res = {};
        assetIds.forEach((assetId) => {
            let info = this.getById(assetId);
            if(info)
                res[assetId] = info;
        });

        return res;
    }

    async ensureAssetInfosLoadedForProducts(products: Product[]): Promise<any>
    {
        const assetIds = products.reduce((assetIdsOut, product) => assetIdsOut.concat(product.assets_3d), []);
        return this.ensureAssetInfosLoadedFor(assetIds);
    }

    // Returns a promise which resolves with the number of assets loaded.
    async ensureAssetInfosLoadedFor(assetIdsIn: number | number[], onBeforeLoadCallback = null): Promise<any>
    {
        let assetIds;
        if(Array.isArray(assetIdsIn))
            assetIds = CoreUtils.makeArrayUnique(assetIdsIn);
        else
            assetIds = [assetIdsIn];

        assetIds = assetIds.filter(assetId => assetId !== null && assetId !== undefined && assetId > 5 && !Number.isNaN(parseInt(assetId)));
        assetIds = assetIds.map(assetId => parseInt(assetId));
        assetIds = CoreUtils.makeArrayUnique(assetIds);

        let allAssetIdsList = assetIds.slice();

        // Filter out the ones that have already been loaded
        assetIds = assetIds.filter(assetId => !this.getById(assetId) && !this.invalidAssetIdsToIgnore[assetId]);
        if(assetIds.length === 0)
            return Promise.resolve({assetInfos: this.getAssetInfos(allAssetIdsList), numLoaded: 0});

        // Exclude already loading assets
        let assetIdsToLoad = assetIds.filter(assetId => !this.loadingPromises[assetId]);

        let promise = null;
        if(assetIdsToLoad.length > 0)
        {
            if(onBeforeLoadCallback)
                onBeforeLoadCallback();
            const assetIdsList = assetIdsToLoad.reduce((accum, assetId) => { return accum + `&asset_ids=${assetId}`; }, '');
            promise = apiFetch<any>(`${EnvConfig.getNewAssetLibBaseUrl()}assets3d?${ASSET_DOWNLOAD_FLAGS}${assetIdsList}`).then((results) => {
                results.forEach(assetInfo => { this.addAssetFromInfo(assetInfo); });
                window.parametricFixtureAssetProvider.resetCaches();
                assetIdsToLoad.forEach((assetId) => {
                    delete this.loadingPromises[assetId];
                    if(!this.getById(assetId))
                        this.invalidAssetIdsToIgnore[assetId] = true;
                });

                return results.length;
            });
        }

        let promisesNeeded = [];
        assetIds.forEach((assetId) => {
            if(promise && !this.loadingPromises[assetId])
                this.loadingPromises[assetId] = promise;
            promisesNeeded.push(this.loadingPromises[assetId]);
        });

        let uniquePromises = CoreUtils.makeArrayUnique(promisesNeeded);
        return Promise.allSettled(uniquePromises).then(results => {

            assetIds.forEach((assetId) => {
                delete this.loadingPromises[assetId];
            });

            let numLoadedAssets = results.reduce((accum, currResult) => { 
                return accum + (currResult.status === 'fulfilled' ? currResult.value : 0); 
                }, 0);
            return {assetInfos: this.getAssetInfos(allAssetIdsList), numLoaded: numLoadedAssets};
        });
    }
}
